import {
  IMAGE_MAX_FILE_SIZE_MB,
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  VIDEO_MAX_FILE_SIZE_GB,
} from '../dropzone.yup';
import { hasImage, hasVideo } from './file';

const FORCE_SINGULAR = 1;
const FORCE_PLURAL = 100;

const formatAcceptedExtensionsArray = (
  acceptedExtensions: string[],
): string => {
  const uppercaseFileExtensions = acceptedExtensions.map((fileExtension) =>
    fileExtension.toUpperCase(),
  );
  if (uppercaseFileExtensions.length === 0) return '';
  if (uppercaseFileExtensions.length === 1) return uppercaseFileExtensions[0];

  const lastItem = uppercaseFileExtensions.pop() as string;

  return `${uppercaseFileExtensions.join(', ')} or ${lastItem}`;
};

const getMediaTypeLabel = (
  hasImages: boolean,
  hasVideos: boolean,
  fileCount: number,
) => {
  if (hasImages && hasVideos) return fileCount > 1 ? 'files' : 'file';
  if (hasImages) return fileCount > 1 ? 'frames' : 'frame';
  if (hasVideos) return fileCount > 1 ? 'videos' : 'video';
  return fileCount > 1 ? 'files' : 'file';
};

const prepareMessageData = (
  acceptedExtensions: string,
  newFilesCounter = 1,
) => {
  const acceptedExtensionsArray = acceptedExtensions.split(',');

  const hasImages = hasImage(acceptedExtensionsArray);
  const hasVideos = hasVideo(acceptedExtensionsArray);

  const formattedTypes = formatAcceptedExtensionsArray(acceptedExtensionsArray);

  const mediaTypeLabel = getMediaTypeLabel(
    hasImages,
    hasVideos,
    newFilesCounter,
  );

  const mediaTypeLabelPlural = getMediaTypeLabel(
    hasImages,
    hasVideos,
    FORCE_PLURAL,
  );

  const mediaTypeLabelSingular = getMediaTypeLabel(
    hasImages,
    hasVideos,
    FORCE_SINGULAR,
  );

  const prefix = newFilesCounter > 1 ? 'One or more' : 'Selected';

  return {
    hasImages,
    hasVideos,
    formattedTypes,
    mediaTypeLabel,
    mediaTypeLabelPlural,
    mediaTypeLabelSingular,
    prefix,
  };
};

export const formatDropzoneLabel = (
  maxFiles: number,
  acceptedExtensions: string,
) => {
  const { hasImages, hasVideos, formattedTypes, mediaTypeLabel } =
    prepareMessageData(acceptedExtensions, maxFiles);

  if (hasImages && hasVideos) {
    return `Your ${mediaTypeLabel} should be in format ${formattedTypes}. Frames must be under ${IMAGE_MAX_FILE_SIZE_MB}MB and videos under ${VIDEO_MAX_FILE_SIZE_GB}GB.`;
  }

  if (hasImages) {
    return `Your ${mediaTypeLabel} should be in format ${formattedTypes} and shouldn't be bigger than ${IMAGE_MAX_FILE_SIZE_MB}MB.`;
  }

  if (hasVideos) {
    return `Your ${mediaTypeLabel} should be in format ${formattedTypes} and shouldn't be bigger than ${VIDEO_MAX_FILE_SIZE_GB}GB.`;
  }

  return `Your ${mediaTypeLabel} shouldn't be bigger than ${IMAGE_MAX_FILE_SIZE_MB}MB.`;
};

export const formatDuplicateFileErrorMessage = (acceptedExtensions: string) => {
  const { mediaTypeLabelPlural, mediaTypeLabelSingular } =
    prepareMessageData(acceptedExtensions);

  return `Duplicate ${mediaTypeLabelPlural} detected. Each ${mediaTypeLabelSingular} can only be uploaded once per exam.`;
};

export const formatFileCountErrorMessage = (
  maxFiles: number,
  acceptedExtensions: string,
) => {
  const { mediaTypeLabel } = prepareMessageData(acceptedExtensions, maxFiles);

  return `Please select fewer ${mediaTypeLabel} so that the total does not exceed ${maxFiles}.`;
};

export const formatFileExtensionsErrorMessage = (
  newFilesCounter: number,
  acceptedExtensions: string,
) => {
  const { formattedTypes, mediaTypeLabel, prefix } = prepareMessageData(
    acceptedExtensions,
    newFilesCounter,
  );

  const verb = newFilesCounter > 1 ? 'have' : 'has';

  return `${prefix} ${mediaTypeLabel} ${verb} an unsupported file format. Only ${formattedTypes} formats are accepted.`;
};

export const formatFileExtensionsMatchTypeErrorMessage = (
  newFilesCounter: number,
  acceptedExtensions: string,
) => {
  const { formattedTypes, mediaTypeLabel, prefix } = prepareMessageData(
    acceptedExtensions,
    newFilesCounter,
  );

  const plural = newFilesCounter > 1;
  const verb = plural ? 'appear' : 'appears';

  return `${prefix} ${mediaTypeLabel} ${verb} to be ${plural ? '' : 'a '}${formattedTypes} ${plural ? 'files' : 'file'}, but ${plural ? 'they are' : 'it is'} not saved in a valid format. Please make sure your ${plural ? 'files are' : 'file is'} properly saved as ${formattedTypes}.`;
};

export const formatFileSizeErrorMessage = (
  newFilesCounter: number,
  hasOversizedImages: boolean,
  hasOversizedVideos: boolean,
) => {
  const mediaTypeLabel = getMediaTypeLabel(
    hasOversizedImages,
    hasOversizedVideos,
    newFilesCounter,
  );

  const prefix = newFilesCounter > 1 ? 'One or more' : 'Selected';
  const verb = newFilesCounter > 1 ? 'exceed' : 'exceeds';

  if (hasOversizedImages && hasOversizedVideos) {
    return `${prefix} ${mediaTypeLabel} ${verb} the maximum file size. Frames must be under ${IMAGE_MAX_FILE_SIZE_MB}MB and videos under ${VIDEO_MAX_FILE_SIZE_GB}GB.  Please upload smaller ${mediaTypeLabel}.`;
  }

  if (hasOversizedImages) {
    return `${prefix} ${mediaTypeLabel} ${verb} the maximum file size of ${IMAGE_MAX_FILE_SIZE_MB}MB. Please upload smaller ${mediaTypeLabel}.`;
  }

  if (hasOversizedVideos) {
    return `${prefix} ${mediaTypeLabel} ${verb} the maximum file size of ${VIDEO_MAX_FILE_SIZE_GB}GB. Please upload smaller ${mediaTypeLabel}.`;
  }

  return `${prefix} ${mediaTypeLabel} ${verb} the maximum file size of ${IMAGE_MAX_FILE_SIZE_MB}MB. Please upload smaller ${mediaTypeLabel}.`;
};

export const formatImageResolutionErrorMessage = (newFilesCounter: number) => {
  const mediaTypeLabel = getMediaTypeLabel(true, false, newFilesCounter);

  const prefix = newFilesCounter > 1 ? 'One or more' : 'Selected';
  const verb = newFilesCounter > 1 ? 'exceed' : 'exceeds';

  return `${prefix} ${mediaTypeLabel} ${verb} the supported resolution of ${IMAGE_MAX_WIDTH}x${IMAGE_MAX_HEIGHT}.`;
};

export const getUnexpectedErrorMessage = () => {
  return "Some files couldn't be uploaded due to an unknown error.";
};
